/**
 * Stream entity class
 */

import moment from 'moment'
import 'moment-duration-format'

/**
 * StreamFactory
 * returns Stream entity class
 *
 * @class StreamFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Stream} class
 *
 * @ngdoc factory
 * @name mwApp.factory:StreamFactory
 */
/* @ngInject */
export default function StreamFactory (Entity, $http, alertLiteSvc) {
  /* === Stream class === */

  /**
   * Stream model
   *
   * @class Stream
   * @classdesc Stream model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @return {models.Stream} Stream entity
   */
  function Stream () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Stream.prototype = Object.create(Entity.prototype)
  Stream.prototype.constructor = Stream

  /* === Stream instance methods & fields === */

  Object.assign(Stream.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Stream#
     * @type {Object}
     */
    _fields: {
      externalId: String,
      name: String,
      streamType: String, // SETRIX_STREAM_TYPES
      videoType: String, // SETRIX_VIDEO_TYPES
      editEndpoint: String,
      restartCount: Number,
      input: 'StreamInputDTO',
      inputFailover: 'StreamInputDTO',
      outputs: ['StreamOutputDTO'],
      preset: { name: String }, // may be use entity?
      comments: ['StreamCommentDTO'],
      node: 'StreamNodeDTO',
      status: {
        enabled: Boolean,
        workStatus: String, /* SETRIX_STREAM_WORK_STATUS_TYPES */
        startTime: String,
        uptimeSeconds: Number,
        residentSetSizeBytes: String,
        cpuUsagePercent: Number,
        inputStatuses: ['StreamStatusInputDTO'],
        outputStatuses: ['StreamStatusOutputDTO']
      },
      creator: String
    },

    /**
     * url template
     *
     * @memberof models.Stream#
     * @type {String}
     */
    _urlTemplate: 'api/setrix/streams',

    /**
     * public url template
     *
     * @memberof models.Stream#
     * @type {String}
     */
    _publicUrlTemplate: 'setrix/streams',

    /**
     * Prepare object
     *
     * @memberof models.Stream#
     * @override
     */
    _prepare: function () {
      const now = moment()
      if (this.status?.startTime) {
        const end = moment(this.status.startTime)
        const duration = moment.duration(now.diff(end))

        this.status.startTime = duration.format('D [day], H [hour] m [min]')
      }

      if (+this.status?.uptimeSeconds) {
        const end = moment().seconds(this.status.uptimeSeconds)

        this.status.uptimeSeconds = moment.duration(end.diff(now)).format('D [day], H [hour] m [min]')
      }
    }

  })

  /* === Stream static methods & fields === */

  /**
   * Groups stream bindings by Setrix server for content-delivery batch APIs.
   *
   * @param {Array<{ streamId?: *, server?: { id?: * } }>} bindings
   * @returns {{ serverId: *, streamIds: string[] }[]}
   */
  Stream.buildAssignedTranscodersFromBindings = function (bindings) {
    if (!Array.isArray(bindings) || !bindings.length) {
      return []
    }
    const streamsByServerId = new Map()
    bindings.forEach((binding) => {
      if (!binding?.streamId) return
      const serverId = binding?.server?.id != null ? binding.server.id : 0
      const sid = String(binding.streamId)
      if (!streamsByServerId.has(serverId)) {
        streamsByServerId.set(serverId, new Set())
      }
      streamsByServerId.get(serverId).add(sid)
    })
    return Array.from(streamsByServerId.entries()).map(([serverId, streamIdSet]) => ({
      serverId,
      streamIds: Array.from(streamIdSet)
    }))
  }

  /**
   * Groups transcoding overview rows by Setrix server for batch stream status API.
   *
   * @param {Array<{ serverId?: *, transcoderId?: * }>} rows
   * @returns {{ serverId: *, streamIds: string[] }[]}
   */
  Stream.buildAssignedTranscodersFromOverviewRows = function (rows) {
    if (!Array.isArray(rows) || !rows.length) {
      return []
    }
    const streamsByServerId = new Map()
    rows.forEach((row) => {
      const streamId = row?.transcoderId
      if (streamId == null || streamId === '') return
      const serverId = row?.serverId != null ? row.serverId : 0
      const sid = String(streamId)
      if (!streamsByServerId.has(serverId)) {
        streamsByServerId.set(serverId, new Set())
      }
      streamsByServerId.get(serverId).add(sid)
    })
    return Array.from(streamsByServerId.entries()).map(([serverId, streamIdSet]) => ({
      serverId,
      streamIds: Array.from(streamIdSet)
    }))
  }

  /**
   * Collects output playback URIs from a loadOutputs API response (per-stream items with outputs[]).
   * Each output may expose a single `uri` or the StreamOutputDTO shape with `uris: [{ uri, type }, …]`
   * (see streamOutputDTO.model.js).
   *
   * @param {Array|Object} items — top-level array from payload, or a wrapper with payload/items/data
   * @returns {string[]}
   */
  Stream.collectOutputUris = function (items) {
    let list = items
    if (items != null && !Array.isArray(items)) {
      if (Array.isArray(items.payload)) list = items.payload
      else if (Array.isArray(items.items)) list = items.items
      else if (Array.isArray(items.data)) list = items.data
      else list = []
    }
    if (!Array.isArray(list)) list = []

    const out = []
    const seen = new Set()
    const add = (u) => {
      if (typeof u !== 'string' || !u.trim()) return
      if (seen.has(u)) return
      seen.add(u)
      out.push(u)
    }
    const extractFromOutput = (output) => {
      if (!output) return
      if (typeof output.uri === 'string') {
        add(output.uri)
        return
      }
      if (Array.isArray(output.uris)) {
        for (const u of output.uris) {
          if (u && typeof u.uri === 'string') add(u.uri)
        }
      }
    }

    for (const it of list) {
      const outs = Array.isArray(it?.outputs) ? it.outputs : []
      for (const o of outs) {
        extractFromOutput(o)
      }
    }
    return out
  }

  Stream.loadStatuses = function (streamIds, params, options = {}) {
    const silent = !!options.silent
    return $http.post('/nora/api/content-delivery/servers/streams/statuses', params)
      .then((response) => response?.data?.payload ?? response?.data)
      .catch((rejection) => {
        if (!silent) {
          alertLiteSvc.alertError(rejection?.statusText || 'Failed to load stream statuses')
        }
        return []
      })
  }

  Stream.loadOutputs = function (streamIds, params) {
    return $http.post('/nora/api/content-delivery/servers/streams/outputs', params)
      .then((response) => response?.data?.payload ?? response?.data)
      .catch((rejection) => {
        alertLiteSvc.alertError(rejection?.statusText || 'Failed to load stream outputs')
      })
  }

  // copy all static methods from Entity class
  Object.assign(Stream, Entity)
  Stream._name = 'Stream'
  Stream.init()

  // permissions
  Stream.access = {}

  return Stream
}
